import { ensureModuleLoadedWithSharedState, isModuleLoaded as isSharedModuleLoaded, markModulesLoaded } from "./moduleTranslationLoadState";
import { mergeTranslations, type TranslationDictionary } from "./mergeTranslations";
import { DEFAULT_LOCALE, parseSupportedLocales, type SupportedLocale } from "./locales";
import { resolveLocale } from "./resolveLocale";
import { useCallback, useEffect, useState } from "react";

type InitialCultureState<ModuleName extends string> = {
  locale: SupportedLocale;
  translations: TranslationDictionary;
  preloadedModules?: ModuleName[];
};

type UseCultureProviderStateOptions<ModuleName extends string> = {
  supportedLocalesRaw?: string | null;
  loadCommonTranslations: (locale: SupportedLocale) => Promise<TranslationDictionary>;
  loadModuleTranslations: (locale: SupportedLocale, moduleName: ModuleName) => Promise<TranslationDictionary>;
  initialCulture?: InitialCultureState<ModuleName>;
  logger?: Pick<Console, "error">;
};

type UseCultureProviderStateResult<ModuleName extends string> = {
  locale: SupportedLocale;
  translations: TranslationDictionary;
  isCommonReady: boolean;
  ensureModuleLoaded: (moduleName: ModuleName) => Promise<void>;
  isModuleLoaded: (moduleName: ModuleName) => boolean;
};

export function useCultureProviderState<ModuleName extends string>(
  options: UseCultureProviderStateOptions<ModuleName>,
): UseCultureProviderStateResult<ModuleName> {
  const { supportedLocalesRaw, loadCommonTranslations, loadModuleTranslations, initialCulture, logger = console } = options;

  const hasInitialLocaleTranslations =
    initialCulture !== undefined && Object.keys(initialCulture.translations[initialCulture.locale] ?? {}).length > 0;

  const [translations, setTranslations] = useState<TranslationDictionary>(initialCulture?.translations ?? {});
  const [locale, setLocale] = useState<SupportedLocale>(initialCulture?.locale ?? DEFAULT_LOCALE);
  const [isCommonReady, setIsCommonReady] = useState(hasInitialLocaleTranslations);

  const mergeIntoTranslations = useCallback((next: TranslationDictionary) => {
    setTranslations((prev) => mergeTranslations(prev, next));
  }, []);

  const ensureModuleLoaded = useCallback(
    async (moduleName: ModuleName) => {
      try {
        const moduleTranslations = await ensureModuleLoadedWithSharedState(locale, moduleName, loadModuleTranslations);
        if (moduleTranslations) {
          mergeIntoTranslations(moduleTranslations);
        }
      } catch (error) {
        logger.error(`Error loading module '${moduleName}' translations for locale ${locale}:`, error);
      }
    },
    [locale, loadModuleTranslations, logger, mergeIntoTranslations],
  );

  const isModuleLoaded = useCallback(
    (moduleName: ModuleName) => {
      return isSharedModuleLoaded(locale, moduleName);
    },
    [locale],
  );

  useEffect(() => {
    if (!initialCulture || (initialCulture.preloadedModules?.length ?? 0) === 0) {
      return;
    }

    markModulesLoaded(initialCulture.locale, initialCulture.preloadedModules ?? []);
  }, [initialCulture]);

  useEffect(() => {
    if (isCommonReady) {
      return;
    }

    const initializeCommonTranslations = async () => {
      const allowedLocales = parseSupportedLocales(supportedLocalesRaw);
      const resolvedLocale = resolveLocale(allowedLocales);
      setLocale(resolvedLocale);

      try {
        const loadedTranslations = await loadCommonTranslations(resolvedLocale);
        mergeIntoTranslations(loadedTranslations);
      } catch (error) {
        logger.error(`Error initializing common translations for locale ${resolvedLocale}:`, error);
      }

      setIsCommonReady(true);
    };

    void initializeCommonTranslations();
  }, [isCommonReady, supportedLocalesRaw, loadCommonTranslations, logger, mergeIntoTranslations]);

  return {
    locale,
    translations,
    isCommonReady,
    ensureModuleLoaded,
    isModuleLoaded,
  };
}
